import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { divide } from "@aprao/common/src/math/divide.js";
import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import { multiply } from "@aprao/common/src/math/multiply.js";
import { wrapFraction } from "@aprao/common/src/math/wrapFraction.js";
import { mapValues } from "@aprao/common/src/object/mapValues.js";
import type {
  Fraction,
  WithRelativesSet,
} from "@aprao/common/src/shapes/common.js";
import { makeAutoObservable } from "mobx";

type NominatorProvidersByKey<TKeys extends string> = Record<
  TKeys,
  () => BigFraction | null
>;

export class WithRelativesSetDelegate<TKeys extends string> {
  readonly $model;

  readonly _providers;

  constructor(
    model: WithRelativesSet<TKeys>,
    providers: NominatorProvidersByKey<TKeys>,
  ) {
    makeAutoObservable(this);

    this.$model = model;
    this._providers = providers;
  }

  get computed(): Record<TKeys, BigFraction | null> {
    // we need to help ts here to pick the right lodash overload
    return mapValues(this._providers, (currentNominator, currentKey) => {
      const otherEntries = Object.entries(this.$model.relatives).filter(
        ([key]) => key !== currentKey,
      ) as Array<[TKeys, Fraction | null]>;

      // Missing keys are treated as if not set to minimise errors
      // but this is a sign of broken data.
      let currentValue = wrapFraction(
        this.$model.relatives[currentKey] ?? null,
      );

      // If the current value is directly available, find the relative in the set
      // that is defined and derive the value from it.
      for (const [otherKey, otherValue] of otherEntries) {
        if (currentValue != null) {
          break;
        }

        // Skip to the next iteration if value is null since we won't be able to
        // computed anything any way.
        if (otherValue == null) {
          continue;
        }

        const denominator = currentNominator();
        const otherProvider = this._providers[otherKey];

        currentValue = divide(
          multiply([wrapFraction(otherValue), otherProvider()]),
          denominator === null || FRACTION_ZERO.equals(denominator) ?
            null
          : denominator,
        );
      }

      return currentValue;
    });
  }

  getRelative(key: TKeys) {
    return this.computed[key] ?? null;
  }

  setRelative(key: TKeys, value: BigFraction | null) {
    this.$model.relatives = mapValues(this.$model.relatives, (_, entryKey) =>
      value == null || entryKey !== key ? null : value.toFraction(),
    );
  }
}
